import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardActionArea,
  CardMedia,
  Typography,
  useMediaQuery,
} from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import { noop } from 'lodash/fp';
import { useSelector } from 'react-redux';
import { businesses, loyalties, memberships } from '../../state';

const classes = {
  giveBackBox: {
    minWidth: '50px',
    backgroundColor: (theme) => theme.palette.primary.main,
    color: (theme) => theme.palette.primary.contrastText,
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 3,
    borderTopLeftRadius: '16px',
    borderBottomRightRadius: '16px',
  },
  card: {
    position: 'relative',
    borderRadius: '16px',
  },
  cardMedia: {
    width: '100%',
    aspectRatio: '16 / 9',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    borderRadius: '16px',
  },
  standAloneCard: {
    width: '100%',
    aspectRatio: '16 / 9',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  },
  square: {
    borderRadius: '0',
    border: 'none',
  },
};

const CustomImgCard = ({
  name,
  imageUrl,
  giveBack,
  details,
  elevation,
  variant,
  onClick,
  clickable,
  square,
  width,
  isLoading,
  ariaLabel,
  bgPosition,
}) => {
  const business = useSelector(businesses.selectors.selectBusiness);
  const membershipConfig = useSelector(memberships.selectors.selectConfigs);
  const loyaltyProgram = useSelector(loyalties.selectors.selectProgram);
  const resolvedBrandColors =
    membershipConfig?.brandColors ||
    loyaltyProgram?.brandColors ||
    business?.brandColors ||
    {};
  const showPrimaryImage = resolvedBrandColors.showFormPrimaryImage !== false;
  const giveBackPercent = `${parseFloat((giveBack * 100).toFixed(2))}%`;
  const isMobile = useMediaQuery('(max-width:960px)');
  const [is16by9, setIs16by9] = useState(true);

  React.useEffect(() => {
    if (!imageUrl) return;

    const img = new Image();
    img.src = imageUrl;
    img.onload = () => {
      const ratio = img.width / img.height;
      const expectedRatio = 16 / 9;
      const isCloseEnough = Math.abs(ratio - expectedRatio) < 0.05;
      setIs16by9(isCloseEnough);
    };
  }, [imageUrl]);

  if (!showPrimaryImage) return null;

  return (
    <>
      <Card
        variant={variant}
        square={square}
        elevation={elevation}
        sx={square ? classes.square : classes.card}
      >
        {giveBack ? (
          <Box sx={classes.giveBackBox}>
            <Typography display="block" align="center" variant="subtitle1">
              {giveBackPercent}
            </Typography>
          </Box>
        ) : null}

        {clickable ? (
          <CardActionArea aria-label={ariaLabel} onClick={onClick}>
            {isLoading || !imageUrl ? (
              <Skeleton variant="rect" sx={classes.cardMedia} />
            ) : (
              <CardMedia sx={classes.cardMedia} alt={name} image={imageUrl} />
            )}
          </CardActionArea>
        ) : (
          <CardMedia
            sx={{
              ...classes.standAloneCard,
              ...(square ? classes.square : {}),
            }}
            alt={name}
            image={imageUrl}
            style={{
              width: width || '100%',
              backgroundPosition: isMobile ? 'center' : bgPosition,
              backgroundSize: is16by9 ? 'contain' : 'cover',
            }}
          />
        )}
      </Card>

      {details && (
        <Typography
          display="block"
          align="center"
          variant="body1"
          color="primary"
        >
          {!name || isLoading ? <Skeleton /> : name}
        </Typography>
      )}
    </>
  );
};

CustomImgCard.defaultProps = {
  name: '',
  imageUrl: '',
  giveBack: 0,
  details: false,
  elevation: 3,
  clickable: false,
  variant: 'outlined',
  onClick: noop,
  square: false,
  width: '',
  isLoading: false,
  ariaLabel: 'clickable image',
  bgPosition: 'center',
};

CustomImgCard.propTypes = {
  name: PropTypes.string,
  imageUrl: PropTypes.string,
  variant: PropTypes.string,
  giveBack: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  details: PropTypes.bool,
  elevation: PropTypes.number,
  clickable: PropTypes.bool,
  onClick: PropTypes.func,
  square: PropTypes.bool,
  width: PropTypes.string,
  isLoading: PropTypes.bool,
  ariaLabel: PropTypes.string,
  bgPosition: PropTypes.string,
};

export default CustomImgCard;
